import type React from "react";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { useEditingClassesByCodebaseId } from "@/store/zustand/designCommandManagerStore";
import { useTypedSelector } from "@/store/store";
import ColorSwatch from "./input/ColorSwatch";
import { useDesignTokensStore } from "@/components/DesignTokens/store/designTokensStore";
import {
  resolveColor,
  resolveColorValue,
} from "@/components/DesignTokens/utils/colorUtils";
import type { CssFieldValue } from "../../index";
import {
  TAILWIND_LOOKUP,
  resolveDesignTokenColor,
  hasBackgroundClasses,
  extractCustomColorFromClass,
  getDarkTokenLookupClass,
} from "@/tailwind/tailwindUtils";
import { useCanvasId } from "@/utils/utils";
import { useSelectedNavTreeNode } from "@/components/Canvas/stores/canvasStore";
import type { ColorGroup, ColorToken } from "@/components/DesignTokens/types";

interface BackgroundColorSwatchProps {
  size?: "sm" | "md" | "lg";
  className?: string;
  selectedCodebaseId?: string;
  classList?: string[];
  cssValues?: { [cssName: string]: CssFieldValue };
  displayValue?: string;
  elementClasses?: string[];
}

interface ColorData {
  light?: string;
  dark?: string;
  gradient?: string;
}

/**
 * Simplified color extraction using existing utilities
 */
const extractColorsFromTailwindClasses = (
  canvasId: string,
  tailwindClasses: string[],
  lightColorGroups: ColorGroup[],
  lightColors: ColorToken[],
  darkColorGroups: ColorGroup[],
  darkColors: ColorToken[],
  cssCustomProperties?: {
    root?: Record<string, string>;
    dark?: Record<string, string>;
  } | null,
): ColorData => {
  const extractedColors: ColorData = {};

  // Process background classes
  for (const cls of tailwindClasses) {
    if (cls.startsWith("bg-") && !cls.startsWith("dark:")) {
      // Check for custom bracket colors first
      const customColor = extractCustomColorFromClass(cls);
      if (customColor) {
        extractedColors.light = customColor;
        extractedColors.dark = customColor; // Custom colors are same in both modes
        continue;
      }

      // Use existing utility for design token resolution first
      const resolvedColor = resolveDesignTokenColor(
        [cls],
        lightColorGroups,
        lightColors,
        darkColorGroups,
        darkColors,
        false,
      );

      if (resolvedColor) {
        extractedColors.light = resolvedColor;

        // Check for dark variant using the same design token resolution
        const darkResolvedColor = resolveDesignTokenColor(
          [cls],
          lightColorGroups,
          lightColors,
          darkColorGroups,
          darkColors,
          true,
        );
        if (darkResolvedColor) {
          extractedColors.dark = darkResolvedColor;
        }
      } else {
        // Fallback to CSS variable resolution using existing utility
        const colorKey = cls.replace("bg-", "");
        const cssVarValue = `var(--${colorKey})`;

        // Use resolveColorValue for light mode
        const lightResolution = resolveColorValue(
          cssVarValue,
          cssCustomProperties?.root,
        );
        if (!lightResolution.hasError) {
          extractedColors.light = lightResolution.hexColor;
        }

        // Use resolveColorValue for dark mode
        const darkResolution = resolveColorValue(
          cssVarValue,
          cssCustomProperties?.dark,
        );
        if (!darkResolution.hasError) {
          extractedColors.dark = darkResolution.hexColor;
        }

        // Final fallback to other resolution methods
        if (!extractedColors.light) {
          const fallbackColor =
            resolveColor(canvasId, [cls]) || TAILWIND_LOOKUP[cls];
          if (fallbackColor) {
            extractedColors.light = fallbackColor;
            if (!extractedColors.dark) {
              extractedColors.dark = fallbackColor; // Use same color for dark if no dark variant
            }
          }
        }
      }
    } else if (cls.startsWith("dark:bg-")) {
      const baseClass = cls.replace("dark:", "");

      // Check for custom bracket colors first
      const customColor = extractCustomColorFromClass(baseClass);
      if (customColor) {
        extractedColors.dark = customColor;
        continue;
      }

      const tokenLookupClass = getDarkTokenLookupClass(cls);

      const resolvedColor = resolveDesignTokenColor(
        [tokenLookupClass],
        lightColorGroups,
        lightColors,
        darkColorGroups,
        darkColors,
        true,
      );

      if (resolvedColor) {
        extractedColors.dark = resolvedColor;
      } else {
        // Use resolveColorValue for CSS variable resolution
        const colorKey = baseClass.replace("bg-", "");
        const cssVarValue = `var(--${colorKey})`;

        const darkResolution = resolveColorValue(
          cssVarValue,
          cssCustomProperties?.dark,
        );
        if (!darkResolution.hasError) {
          extractedColors.dark = darkResolution.hexColor;
        } else {
          // Fallback to other resolution methods
          const fallbackColor =
            resolveColor(canvasId, [cls]) || TAILWIND_LOOKUP[baseClass];
          if (fallbackColor) {
            extractedColors.dark = fallbackColor;
          }
        }
      }
    }
  }

  return extractedColors;
};

/**
 * Extracts color from CSS string (fallback method)
 */
const extractColorFromCSS = (css: string): string | null => {
  const colorMatch = css.match(
    /(?:background-color|--tw-bg-opacity):\s*([^;]+)/,
  );
  if (colorMatch) {
    return colorMatch[1].trim();
  }
  return null;
};

export const useBackgroundDisplayInfo = (cssValues?: {
  [cssName: string]: CssFieldValue;
}) => {
  return useMemo(() => {
    // Check for background image
    const bgImage = cssValues?.["background-image"]?.evaluatedValue;
    const hasImage = bgImage?.includes("url");
    const hasGradient = bgImage?.includes("gradient");

    // Background needs full width when it has text labels (Image or Gradient)
    const needsFullWidth = hasImage || hasGradient;

    return {
      hasImage,
      hasGradient,
      needsFullWidth,
      displayText: hasImage ? "Image" : hasGradient ? "Gradient" : null,
    };
  }, [cssValues]);
};

const BackgroundColorSwatch: React.FC<BackgroundColorSwatchProps> = ({
  size = "md",
  className = "",
  selectedCodebaseId,
  classList,
  cssValues,
  displayValue,
  elementClasses: propElementClasses,
}) => {
  const { selectedElement } = useSelectedNavTreeNode();
  const isCanvasDarkMode = useDarkModeStore((state) => state.isCanvasDarkMode);
  const lightColorGroups = useDesignTokensStore(
    (state) => state.lightColorGroups,
  );
  const lightColors = useDesignTokensStore((state) => state.lightColors);
  const darkColorGroups = useDesignTokensStore(
    (state) => state.darkColorGroups,
  );
  const darkColors = useDesignTokensStore((state) => state.darkColors);
  const cssCustomProperties = useTypedSelector(
    (state) => state.iframe.cssCustomProperties,
  );

  const canvasId = useCanvasId();

  const { classes: fetchedElementClasses } = useEditingClassesByCodebaseId(
    classList || [],
    selectedCodebaseId || "",
  );

  // Use prop classes if available (more up-to-date), fallback to fetched classes
  const elementClasses = propElementClasses ?? fetchedElementClasses;

  // Get background display info using the shared hook
  const { displayText } = useBackgroundDisplayInfo(cssValues);

  // Calculate current color to display
  const { currentColor, isImage, isGradient } = useMemo(() => {
    if (!selectedElement?.codebaseId) {
      return { currentColor: undefined, isImage: false, isGradient: false };
    }

    // 1. First priority: CSS background-image (most reliable for gradients/images)
    const bgImage = cssValues?.["background-image"]?.evaluatedValue;
    if (bgImage?.includes("gradient")) {
      return { currentColor: bgImage, isImage: false, isGradient: true };
    }

    if (bgImage?.includes("url")) {
      return { currentColor: bgImage, isImage: true, isGradient: false };
    }

    // 2. Second priority: Tailwind classes for solid colors (responds immediately to dark mode)
    if (elementClasses.length > 0) {
      // Only extract if we have specific background classes or gradient classes
      const hasBackgroundClass = hasBackgroundClasses(elementClasses);

      if (hasBackgroundClass) {
        const extractedColors = extractColorsFromTailwindClasses(
          canvasId,
          elementClasses,
          lightColorGroups,
          lightColors,
          darkColorGroups,
          darkColors,
          cssCustomProperties,
        );

        if (extractedColors.gradient) {
          return {
            currentColor: extractedColors.gradient,
            isImage: false,
            isGradient: true,
          };
        }

        const currentMode = isCanvasDarkMode ? "dark" : "light";
        const modeColor = extractedColors[currentMode];

        if (modeColor) {
          return { currentColor: modeColor, isImage: false, isGradient: false };
        }

        // Fallback to other mode
        const fallbackMode = currentMode === "light" ? "dark" : "light";
        const fallbackColor = extractedColors[fallbackMode];
        if (fallbackColor) {
          return {
            currentColor: fallbackColor,
            isImage: false,
            isGradient: false,
          };
        }
      }
    }

    // 3. Fallback priority: CSS background-color for solid colors
    const bgColor = cssValues?.["background-color"]?.evaluatedValue;
    if (bgColor) {
      return { currentColor: bgColor, isImage: false, isGradient: false };
    }

    return { currentColor: undefined, isImage: false, isGradient: false };
  }, [
    canvasId,
    selectedElement?.codebaseId,
    cssValues,
    elementClasses,
    isCanvasDarkMode,
    lightColorGroups,
    lightColors,
    darkColorGroups,
    darkColors,
    cssCustomProperties,
  ]);

  if (!currentColor && !isImage && !isGradient) {
    return null;
  }
  if (isImage || isGradient) {
    return (
      <div className="flex items-center gap-2 w-full">
        {isImage ? (
          <div
            className="w-4 h-4 rounded-xs bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: currentColor,
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            }}
          />
        ) : (
          <ColorSwatch color={currentColor || ""} size={size} />
        )}
        <div className="text-xs font-medium text-secondary-n-50 truncate">
          {displayText || displayValue}
        </div>
      </div>
    );
  }

  return (
    <ColorSwatch color={currentColor || ""} size={size} className={className} />
  );
};

// Export the component and utility functions
export {
  BackgroundColorSwatch,
  extractColorsFromTailwindClasses,
  extractColorFromCSS,
};
export type { ColorData };
export default BackgroundColorSwatch;
